import type { ActivityContentProjectGoalConnection } from "@/api";
import type { Activity } from "@/models/activities";
import type { ActivityHandler } from "../interfaces";

import { feedTitle, goalLink, projectLink } from "../feedItemLinks";

const ProjectGoalConnection: ActivityHandler = {
  pageHtmlTitle(_activity: Activity) {
    throw new Error("Not implemented");
  },

  pagePath(paths, activity: Activity): string {
    return paths.projectPath(content(activity).project!.id!);
  },

  PageTitle(_props: { activity: any }) {
    throw new Error("Not implemented");
  },

  PageContent(_props: { activity: Activity }) {
    throw new Error("Not implemented");
  },

  PageOptions(_props: { activity: Activity }) {
    return null;
  },

  FeedItemTitle({ activity, page }: { activity: Activity; page: any }) {
    const data = content(activity);
    const { project: p, goal: g, previousGoal } = data;
    const connectedGoalName = currentGoalName(data);
    const disconnectedGoalName = previousGoalName(data);

    const project = projectLink(p);

    if (g) {
      const goal = goalLink(g);

      if (page === "проект") {
        return feedTitle(activity, "связал(а) проект с", goal, "цель");
      } else if (page === "цель") {
        return feedTitle(activity, "связал(а)", project, "проект к цели");
      } else {
        return feedTitle(activity, "связал(а)", project, "проект в", goal, "цель");
      }
    }

    if (connectedGoalName) {
      if (page === "проект") {
        return feedTitle(activity, "связал(а) проект с", connectedGoalName, "цель");
      } else {
        return feedTitle(activity, "связал(а)", project, "проект в", connectedGoalName, "цель");
      }
    }

    // Handle cases where a project was disconnected from a goal
    if (previousGoal) {
      const prevGoal = goalLink(previousGoal);

      if (page === "проект") {
        return feedTitle(activity, "отсоединил(а) проект от", prevGoal, "цель");
      } else {
        return feedTitle(activity, "отсоединил(а)", project, "проект из", prevGoal, "цель");
      }
    } else if (disconnectedGoalName) {
      if (page === "проект") {
        return feedTitle(activity, "отсоединил(а) проект от", disconnectedGoalName, "цель");
      } else {
        return feedTitle(activity, "отсоединил(а)", project, "проект из", disconnectedGoalName, "цель");
      }
    }

    // Fallback if no previous goal information is available
    if (page === "проект") {
      return feedTitle(activity, "отсоединил(а) проект от родительской цели");
    } else {
      return feedTitle(activity, "отсоединил(а)", project, "проект от родительской цели");
    }
  },

  FeedItemContent(_props: { activity: Activity; page: any }) {
    return null;
  },

  feedItemAlignment(_activity: Activity): "items-start" | "items-center" {
    return "items-center";
  },

  commentCount(_activity: Activity): number {
    throw new Error("Not implemented");
  },

  hasComments(_activity: Activity): boolean {
    throw new Error("Not implemented");
  },

  NotificationTitle({ activity }: { activity: Activity }) {
    const data = content(activity);
    const projectName = data.project?.name;
    const connectedGoalName = currentGoalName(data);
    const disconnectedGoalName = previousGoalName(data);

    if (connectedGoalName) {
      return projectName
        ? `Connected ${projectName} project to the ${connectedGoalName} goal`
        : `Connected a project to the ${connectedGoalName} goal`;
    }

    if (disconnectedGoalName) {
      return projectName
        ? `Disconnected ${projectName} project from the ${disconnectedGoalName} goal`
        : `Disconnected a project from the ${disconnectedGoalName} goal`;
    }

    return projectName ? `Updated the parent goal of ${projectName} project` : "Родительская цель проекта обновлена";
  },

  NotificationLocation({ activity }: { activity: Activity }) {
    const data = content(activity);

    return data.project?.name ?? currentGoalName(data) ?? previousGoalName(data);
  },
};

function content(activity: Activity): ActivityContentProjectGoalConnection {
  return activity.content as ActivityContentProjectGoalConnection;
}

function currentGoalName(content: ActivityContentProjectGoalConnection): string | null {
  return content.goal?.name ?? content.goalName;
}

function previousGoalName(content: ActivityContentProjectGoalConnection): string | null {
  return content.previousGoal?.name ?? content.previousGoalName;
}

export default ProjectGoalConnection;
